import React from 'react';
import { Camera, Focus, User, Users, ScanEye, Target, Maximize2, Move, ZoomIn, MonitorPlay } from 'lucide-react';
import { AVButton } from './ui/AVButton';
import { CameraMode } from '../types/crestron';
interface CameraPageProps {
  cameraMode: CameraMode;
  activePreset: string | null;
  onToggleAutoTrack: () => void;
  onRecallPreset: (preset: string) => void;
}
export function CameraPage({
  cameraMode,
  activePreset,
  onToggleAutoTrack,
  onRecallPreset
}: CameraPageProps) {
  const presenterPresets = [{
    id: 'stage-left',
    label: 'Stage Left',
    icon: Move
  }, {
    id: 'stage-center',
    label: 'Stage Center',
    icon: Target
  }, {
    id: 'stage-right',
    label: 'Stage Right',
    icon: Move
  }, {
    id: 'stage-wide',
    label: 'Wide Stage',
    icon: Maximize2
  }];
  const audienceGrid = [{
    id: 'aud-fl',
    label: 'Front Left',
    row: 'Front',
    col: 'Left'
  }, {
    id: 'aud-fc',
    label: 'Front Center',
    row: 'Front',
    col: 'Center'
  }, {
    id: 'aud-fr',
    label: 'Front Right',
    row: 'Front',
    col: 'Right'
  }, {
    id: 'aud-ml',
    label: 'Mid Left',
    row: 'Mid',
    col: 'Left'
  }, {
    id: 'aud-mc',
    label: 'Mid Center',
    row: 'Mid',
    col: 'Center'
  }, {
    id: 'aud-mr',
    label: 'Mid Right',
    row: 'Mid',
    col: 'Right'
  }, {
    id: 'aud-bl',
    label: 'Back Left',
    row: 'Back',
    col: 'Left'
  }, {
    id: 'aud-bc',
    label: 'Back Center',
    row: 'Back',
    col: 'Center'
  }, {
    id: 'aud-br',
    label: 'Back Right',
    row: 'Back',
    col: 'Right'
  }];
  return <div className="h-full p-4 grid grid-cols-2 gap-4" data-id="element-139">
      {/* Presenter Camera Control */}
      <div className="bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl p-4 border border-neutral-700 flex flex-col shadow-xl" data-id="element-140">
        <div className="mb-4" data-id="element-141">
          <div className="flex items-center justify-between mb-1.5" data-id="element-142">
            <div className="flex items-center gap-2" data-id="element-143">
              <div className="p-1.5 bg-blue-600/20 rounded-lg" data-id="element-144">
                <User className="text-blue-400" size={18} strokeWidth={2.5} data-id="element-145" />
              </div>
              <div data-id="element-146">
                <h2 className="text-base font-bold text-white leading-none" data-id="element-147">
                  Presenter Camera
                </h2>
                <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-148">
                  Auto-tracking PTZ
                </p>
              </div>
            </div>
            <div className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase flex items-center gap-1 ${cameraMode === CameraMode.Auto ? 'bg-green-900/40 text-green-400 border border-green-700' : 'bg-neutral-900 text-gray-500 border border-neutral-800'}`} data-id="element-149">
              {cameraMode === CameraMode.Auto ? <>
                  <ScanEye size={12} className="animate-pulse" data-id="element-150" />
                  Tracking
                </> : <>
                  <Target size={12} data-id="element-151" />
                  Manual
                </>}
            </div>
          </div>
        </div>

        {/* Auto-Track Toggle */}
        <div className="mb-4" data-id="element-152">
          <AVButton variant={cameraMode === CameraMode.Auto ? 'active' : 'default'} icon={Focus} label="AUTO-TRACKING" subLabel={cameraMode === CameraMode.Auto ? 'Following presenter' : 'Enable auto-track'} onClick={onToggleAutoTrack} fullWidth size="md" data-id="element-153" />
        </div>

        {/* Preset Buttons */}
        <div className="flex-1" data-id="element-154">
          <div className="flex items-center gap-1.5 mb-3" data-id="element-155">
            <Target size={14} className="text-gray-500" data-id="element-156" />
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-157">
              Manual Presets
            </span>
            {cameraMode === CameraMode.Auto && <span className="text-[10px] text-amber-500 ml-auto" data-id="element-158">
                (Disables auto)
              </span>}
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-159">
            {presenterPresets.map(preset => {
            const PresetIcon = preset.icon;
            return <button key={preset.id} onClick={() => onRecallPreset(preset.id)} className={`
                    flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all
                    ${activePreset === preset.id ? 'bg-blue-600 border-blue-400 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]' : 'bg-neutral-900 border-neutral-700 text-gray-400 hover:border-neutral-500 hover:text-gray-200'}
                  `} data-id="element-160">
                  <PresetIcon size={22} strokeWidth={2} className="mb-1" data-id="element-161" />
                  <span className="text-xs font-bold" data-id="element-162">{preset.label}</span>
                </button>;
          })}
          </div>
        </div>

        {/* Camera Info */}
        <div className="mt-3 pt-3 border-t border-neutral-700" data-id="element-163">
          <div className="grid grid-cols-2 gap-2 text-[10px]" data-id="element-164">
            <div className="flex justify-between" data-id="element-165">
              <span className="text-gray-500" data-id="element-166">Status:</span>
              <span className="text-green-400 font-bold" data-id="element-167">ONLINE</span>
            </div>
            <div className="flex justify-between" data-id="element-168">
              <span className="text-gray-500" data-id="element-169">Signal:</span>
              <span className="text-green-400 font-bold" data-id="element-170">1080p60</span>
            </div>
          </div>
        </div>
      </div>

      {/* Audience Camera Control with Stage Visualization */}
      <div className="bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl p-4 border border-neutral-700 flex flex-col shadow-xl" data-id="element-171">
        <div className="mb-3" data-id="element-172">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-173">
            <div className="p-1.5 bg-amber-600/20 rounded-lg" data-id="element-174">
              <Users className="text-amber-400" size={18} strokeWidth={2.5} data-id="element-175" />
            </div>
            <div data-id="element-176">
              <h2 className="text-base font-bold text-white leading-none" data-id="element-177">
                Audience Camera
              </h2>
              <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-178">
                20x Optical Zoom PTZ
              </p>
            </div>
          </div>
        </div>

        {/* Stage & LED Wall Visualization */}
        <div className="mb-3" data-id="element-179">
          <div className="relative bg-neutral-950 rounded-lg border border-neutral-700 p-3" data-id="element-180">
            {/* Stage Label */}
            <div className="flex items-center justify-center gap-2 mb-2" data-id="element-181">
              <MonitorPlay size={12} className="text-blue-400" data-id="element-182" />
              <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-183">
                Stage & LED Wall (Front)
              </span>
            </div>

            {/* LED Wall Visual */}
            <div className="relative h-8 bg-gradient-to-b from-blue-900/30 to-blue-950/30 rounded border-2 border-blue-800/50 flex items-center justify-center mb-2" data-id="element-184">
              <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(59,130,246,0.1)_50%,transparent_100%)] animate-pulse" data-id="element-185"></div>
              <span className="text-[9px] font-bold text-blue-400 uppercase tracking-widest relative z-10" data-id="element-186">
                21:9 LED Wall
              </span>
            </div>

            {/* Stage Platform */}
            <div className="h-6 bg-gradient-to-b from-neutral-800 to-neutral-900 rounded border border-neutral-700 flex items-center justify-center" data-id="element-187">
              <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-188">
                Stage Platform
              </span>
            </div>

            {/* Camera Direction Indicator */}
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex flex-col items-center" data-id="element-189">
              <div className="h-4 w-px bg-amber-500" data-id="element-190"></div>
              <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[6px] border-t-amber-500" data-id="element-191"></div>
              <span className="text-[8px] font-bold text-amber-400 uppercase tracking-wider mt-1" data-id="element-192">
                Camera View
              </span>
            </div>
          </div>
        </div>

        {/* Audience Area Label */}
        <div className="flex items-center justify-center gap-2 mb-2" data-id="element-193">
          <Camera size={12} className="text-amber-400" data-id="element-194" />
          <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-195">
            Audience Area (Tap to Position Camera)
          </span>
        </div>

        {/* 3x3 Preset Grid - Oriented to match room */}
        <div className="flex-1 flex flex-col" data-id="element-196">
          <div className="grid grid-cols-3 gap-2.5 aspect-square max-h-[320px] mx-auto w-full" data-id="element-197">
            {audienceGrid.map((zone, index) => {
            const isActive = activePreset === zone.id;
            return <button key={zone.id} onClick={() => onRecallPreset(zone.id)} className={`
                    relative rounded-lg border-2 flex flex-col items-center justify-center font-bold text-xs transition-all
                    ${isActive ? 'bg-gradient-to-br from-amber-600 to-amber-700 border-amber-400 text-white shadow-[0_0_20px_rgba(245,158,11,0.6)] scale-105 z-10' : 'bg-neutral-900/80 border-neutral-700 text-gray-500 hover:border-amber-500 hover:text-gray-300 hover:bg-neutral-800'}
                  `} data-id="element-198">
                  <ZoomIn size={20} className={`mb-1 ${isActive ? 'text-white' : 'text-gray-600'}`} strokeWidth={2} data-id="element-199" />

                  <div className="text-center" data-id="element-200">
                    <div className={`text-[10px] font-bold ${isActive ? 'text-white' : 'text-gray-400'}`} data-id="element-201">
                      {zone.row}
                    </div>
                    <div className={`text-[9px] ${isActive ? 'text-amber-100' : 'text-gray-600'}`} data-id="element-202">
                      {zone.col}
                    </div>
                  </div>

                  {isActive && <div className="absolute top-1.5 right-1.5" data-id="element-203">
                      <div className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" data-id="element-204"></div>
                    </div>}
                </button>;
          })}
          </div>

          {/* Orientation Helper */}
          <div className="mt-2 flex items-center justify-between px-2 text-[9px] text-gray-600" data-id="element-205">
            <span data-id="element-206">← Left</span>
            <span className="text-gray-500" data-id="element-207">Front to Back →</span>
            <span data-id="element-208">Right →</span>
          </div>
        </div>

        {/* Camera Info */}
        <div className="mt-3 pt-3 border-t border-neutral-700" data-id="element-209">
          <div className="grid grid-cols-2 gap-2 text-[10px]" data-id="element-210">
            <div className="flex justify-between" data-id="element-211">
              <span className="text-gray-500" data-id="element-212">Status:</span>
              <span className="text-green-400 font-bold" data-id="element-213">ONLINE</span>
            </div>
            <div className="flex justify-between" data-id="element-214">
              <span className="text-gray-500" data-id="element-215">Zoom:</span>
              <span className="text-amber-400 font-bold" data-id="element-216">20x OPT</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}